import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import dayjs, { type Dayjs } from "dayjs";

/**
 * DatePicker
 *
 * Self-contained shadcn-style date picker for the standalone app.
 * Stores values as `MM/DD/YYYY` strings — matches the format Elation
 * surfaces in its sandbox UI. `useMemberSearch.buildFilter` converts to
 * ISO (`YYYY-MM-DD`) before sending to the backend.
 *
 * The text input auto-formats as the user types: digits are masked
 * into `MM/DD/YYYY` (slashes inserted at positions 3 and 6, capped at
 * 8 digits). The calendar popover stays available for point-and-click
 * selection — handy for DOBs because the header has month + year
 * dropdowns so users can jump decades without paging.
 *
 * The calendar panel is PORTALED to `document.body` and positioned `fixed`
 * off the trigger's rect, not `absolute` inside it. It has to be: this picker
 * is now mounted inside a Home widget whose `Card` shell is `overflow-hidden`
 * with `rounded-2xl`, which clips an absolutely-positioned child at the card
 * edge — the panel would lose its bottom two-thirds. Portaling also escapes
 * scroll containers and `DialogContent`, so the same component works
 * anywhere. (astrana-ui-modules DESIGN_SYSTEM.md → "Floating panels must be
 * portaled".) Consequence to keep in mind: the panel is no longer a DOM
 * descendant of the trigger, so outside-click detection must test BOTH nodes,
 * and the position must be recomputed while open on scroll/resize.
 */
export interface DatePickerProps {
  id?: string;
  value: string; // MM/DD/YYYY or ""
  onChange: (value: string) => void;
  placeholder?: string;
  /** Earliest selectable year. Defaults to 120 years before today. */
  minYear?: number;
  /** Latest selectable year. Defaults to current year. */
  maxYear?: number;
  className?: string;
  ariaLabel?: string;
  /**
   * Field scale. `"default"` is the full-page form treatment
   * (`rounded-xl px-4 py-3 text-base`); `"compact"` matches the Home widget's
   * inputs (`rounded-lg px-3 py-2 text-sm`). The CALENDAR panel is the same
   * size either way — it's a fixed 19rem popover, and shrinking day cells
   * would only make them harder to hit.
   */
  size?: "default" | "compact";
}

/**
 * Panel footprint, used to place the portaled popover. `PANEL_W` mirrors the
 * `w-[19rem]` class below (304px); `PANEL_H_EST` is a measured upper bound for
 * the tallest state (header + 6-week grid + footer) and is only ever used to
 * DECIDE flip-up vs. drop-down, never to size the panel — so being a few px
 * off shifts nothing.
 */
const PANEL_W = 304;
const PANEL_H_EST = 380;
const PANEL_GAP = 8;
/**
 * Floor for the panel's height cap. Below this the calendar is useless
 * anyway, and a cap of ~0 would collapse it to an invisible sliver — better
 * to overflow a pathologically short viewport than to render nothing.
 */
const MIN_PANEL_H = 200;

/**
 * Where the portaled panel sits, in viewport coordinates. Exactly one of
 * `top` / `bottom` is set: dropping down pins the panel's TOP under the
 * field, flipping up pins its BOTTOM above the field, so a tall calendar
 * grows away from the trigger instead of off-screen.
 */
type PanelPos = {
  left: number;
  top?: number;
  bottom?: number;
  /**
   * Space actually available in the chosen direction. Set ALWAYS, not just
   * when short: a portaled `fixed` panel can't be scrolled into view by the
   * page, so anything past the viewport edge is unreachable — with a cap plus
   * `overflow-y-auto` the calendar scrolls inside itself instead. In a normal
   * window this exceeds the panel's natural height and changes nothing.
   */
  maxHeight: number;
};

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Mask raw user input into a partial MM/DD/YYYY string. Strips
// non-digits, caps at 8 digits, and inserts slashes once each segment
// is filled. Backspace works naturally because we always recompute
// from `digits.length`.
function maskDate(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

// Strict MM/DD/YYYY parser: a complete, real calendar date or null — so a
// partial masked value ("01/01/19") or an impossible one ("13/45/2000",
// "02/29/2001") is null. Avoids pulling in dayjs's customParseFormat plugin
// just for this one format — but without that plugin dayjs IGNORES the strict
// flag and rolls overflow over ("2000-13-45" → 2001-02-14), so the round-trip
// check below is what actually rejects an impossible date.
export function parseDate(s: string): Dayjs | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return null;
  const [, mm, dd, yyyy] = m;
  const iso = `${yyyy}-${mm}-${dd}`;
  const d = dayjs(iso, "YYYY-MM-DD", true);
  return d.isValid() && d.format("YYYY-MM-DD") === iso ? d : null;
}

export function DatePicker({
  id,
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  minYear,
  maxYear,
  className,
  ariaLabel,
  size = "default",
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // `null` until measured. The panel renders only once a position exists, so
  // it never paints one frame at the top-left of the viewport.
  const [panelPos, setPanelPos] = useState<PanelPos | null>(null);

  const compact = size === "compact";

  const today = dayjs();
  const resolvedMaxYear = maxYear ?? today.year();
  const resolvedMinYear = minYear ?? resolvedMaxYear - 120;

  // Selected date (parsed) and the month currently shown in the grid.
  const selected: Dayjs | null = useMemo(() => parseDate(value), [value]);

  const [viewMonth, setViewMonth] = useState<Dayjs>(() => selected ?? today);

  // Re-sync the visible month when the consumer changes `value` externally
  // (e.g. an Elation prefill) — but only while the popover is closed, so
  // we don't yank the calendar out from under a user mid-click.
  useEffect(() => {
    if (open) return;
    if (selected) setViewMonth(selected);
  }, [selected, open]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      // The panel lives in a body-level portal, so it is NOT inside
      // `rootRef` — without this second test every click on a day cell
      // would read as an outside click and close the calendar.
      if (rootRef.current?.contains(t)) return;
      if (panelRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  /**
   * Measure the trigger and place the panel. Reads layout, so it stays out of
   * render — a plain callback invoked on open and re-invoked by the
   * scroll/resize effect below.
   */
  const positionPanel = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - r.bottom;
    // Flip up only when below genuinely can't hold the panel AND above has
    // more room — otherwise a field near the fold would flip on every open.
    const flipUp = spaceBelow < PANEL_H_EST + PANEL_GAP && r.top > spaceBelow;
    // Clamp horizontally so the panel never hangs off either viewport edge
    // (the DOB field sits in the right column of a two-up grid).
    const left = Math.max(
      PANEL_GAP,
      Math.min(r.left, window.innerWidth - PANEL_W - PANEL_GAP),
    );
    setPanelPos(
      flipUp
        ? {
            left,
            bottom: window.innerHeight - r.top + PANEL_GAP,
            maxHeight: Math.max(MIN_PANEL_H, r.top - 2 * PANEL_GAP),
          }
        : {
            left,
            top: r.bottom + PANEL_GAP,
            maxHeight: Math.max(MIN_PANEL_H, spaceBelow - 2 * PANEL_GAP),
          },
    );
  }, []);

  /**
   * Keep the portaled panel glued to its trigger while open. A fixed-position
   * element in a body portal doesn't move with an ancestor's scroll, so this
   * is genuine external-DOM synchronization, not derived state. `capture: true`
   * on scroll is required — Home's widget slot scrolls in its own container,
   * and scroll events don't bubble.
   */
  useEffect(() => {
    if (!open) return;
    positionPanel();
    const onMove = () => positionPanel();
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, positionPanel]);

  // Build a 6-week grid (always 42 cells) for the currently-viewed month.
  const grid = useMemo(() => {
    const firstOfMonth = viewMonth.startOf("month");
    const startOffset = firstOfMonth.day(); // 0 = Sun
    const start = firstOfMonth.subtract(startOffset, "day");
    return Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  }, [viewMonth]);

  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = resolvedMaxYear; y >= resolvedMinYear; y--) out.push(y);
    return out;
  }, [resolvedMinYear, resolvedMaxYear]);

  function pick(d: Dayjs) {
    onChange(d.format("MM/DD/YYYY"));
    setOpen(false);
  }

  function clear() {
    onChange("");
    setOpen(false);
  }

  return (
    // No `relative` here any more: the panel is portaled and positioned off
    // this element's measured rect, so it needs no positioning context.
    <div ref={rootRef} className={className}>
      <div
        className={
          "w-full flex items-center gap-2 border border-input bg-background hover:border-ring " +
          "focus-within:outline-none focus-within:border-primary focus-within:ring-primary/15 " +
          "transition-all duration-150 " +
          (compact
            ? "rounded-lg px-3 py-2 text-sm focus-within:ring-2"
            : "rounded-xl px-4 py-3 text-base focus-within:ring-4")
        }
      >
        <input
          id={id}
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(maskDate(e.target.value))}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoComplete="off"
          maxLength={10}
          className="flex-1 min-w-0 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none"
        />
        <button
          type="button"
          onClick={() => {
            // Measure BEFORE flipping open, so the panel's first frame is
            // already in place; the effect above then keeps it there. Kept
            // OUT of the `setOpen` updater on purpose — React StrictMode
            // double-invokes updaters to check purity, and a measurement
            // that writes state is not a pure updater.
            if (!open) positionPanel();
            setOpen((o) => !o);
          }}
          aria-label="Open calendar"
          aria-haspopup="dialog"
          aria-expanded={open}
          className={
            "shrink-0 inline-flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground " +
            (compact ? "h-5 w-5" : "h-6 w-6")
          }
        >
          <CalendarIcon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      </div>

      {open && panelPos
        ? createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Choose date"
          style={{
            left: panelPos.left,
            ...(panelPos.top !== undefined ? { top: panelPos.top } : {}),
            ...(panelPos.bottom !== undefined ? { bottom: panelPos.bottom } : {}),
            maxHeight: panelPos.maxHeight,
          }}
          className="fixed z-50 w-[19rem] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-xl border border-border bg-popover text-popover-foreground shadow-lg p-3"
        >
          <div className="flex items-center gap-2 mb-2">
            <button
              type="button"
              onClick={() => setViewMonth((m) => m.subtract(1, "month"))}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <select
              value={viewMonth.month()}
              onChange={(e) => setViewMonth((m) => m.month(Number(e.target.value)))}
              className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              aria-label="Month"
            >
              {MONTHS.map((label, i) => (
                <option key={label} value={i}>{label}</option>
              ))}
            </select>

            <select
              value={viewMonth.year()}
              onChange={(e) => setViewMonth((m) => m.year(Number(e.target.value)))}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
              aria-label="Year"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => setViewMonth((m) => m.add(1, "month"))}
              className="h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent text-muted-foreground"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 text-center text-xs text-muted-foreground mb-1">
            {DAYS.map((d) => (
              <div key={d} className="py-1 font-medium">{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {grid.map((d) => {
              const inMonth = d.month() === viewMonth.month();
              const isSelected = selected ? d.isSame(selected, "day") : false;
              const isToday = d.isSame(today, "day");
              return (
                <button
                  key={d.format("YYYY-MM-DD")}
                  type="button"
                  onClick={() => pick(d)}
                  className={[
                    "h-8 w-full rounded-md text-sm transition-colors",
                    isSelected
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "hover:bg-accent hover:text-accent-foreground",
                    !isSelected && !inMonth && "text-muted-foreground/50",
                    !isSelected && inMonth && "text-foreground",
                    !isSelected && isToday && "ring-1 ring-ring",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label={d.format("MMMM D, YYYY")}
                  aria-pressed={isSelected}
                >
                  {d.date()}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-3 pt-2 border-t border-border">
            <button
              type="button"
              onClick={clear}
              className="text-xs font-medium text-muted-foreground hover:text-foreground px-2 py-1 rounded-md"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={() => pick(today)}
              className="text-xs font-medium text-primary hover:underline px-2 py-1 rounded-md"
            >
              Today
            </button>
          </div>
        </div>,
            document.body,
          )
        : null}
    </div>
  );
}
